import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useChildDocuments, useSalesDocument } from '@/hooks/use-sales';
import type { SalesDocument, SalesDocType } from '@erp/data-access/interfaces';
import { Badge } from '@erp/ui';
import { Card, CardContent } from '@erp/ui';
import { ArrowUp, ArrowDown } from '@/components/icons';

const DOC_TYPE_LABELS: Record<SalesDocType, string> = {
  quote: 'sales.quote',
  order: 'sales.order',
  delivery: 'sales.delivery',
  invoice: 'sales.invoice',
  credit_note: 'sales.creditNote',
};

function getDocPath(doc: SalesDocument): string {
  const map: Record<SalesDocType, string> = {
    quote: '/sales/quotes',
    order: '/sales/orders',
    delivery: '/sales/deliveries',
    invoice: '/sales/invoices',
    credit_note: '/sales/credit-notes',
  };
  return `${map[doc.docType]}/${doc.id}`;
}

function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

/** Show a single parent document link */
function ParentDocLink({ parentId }: { parentId: string }) {
  const { t } = useTranslation();
  const { data: parentDoc } = useSalesDocument(parentId);

  if (!parentDoc) return null;

  return (
    <Link
      to={getDocPath(parentDoc)}
      className="block rounded-lg border px-3 py-2.5 hover:bg-accent transition-colors"
    >
      {/* Row 1: Badge + Number + Amount (mobile & desktop) */}
      <div className="flex items-center gap-2 min-w-0">
        <ArrowUp size={14} className="text-muted-foreground shrink-0" />
        <Badge variant="outline" className="shrink-0">{t(DOC_TYPE_LABELS[parentDoc.docType])}</Badge>
        <span className="text-sm font-medium truncate">{parentDoc.number}</span>
        <span className="ms-auto text-sm font-medium whitespace-nowrap">{formatPrice(parentDoc.totalTtc)} DT</span>
      </div>
      {/* Row 2: Date (secondary info) */}
      <div className="mt-1 ps-[calc(14px+0.5rem)] text-xs text-muted-foreground">
        {formatDate(parentDoc.docDate)}
      </div>
    </Link>
  );
}

interface LinkedDocumentsProps {
  document: SalesDocument;
}

export function LinkedDocuments({ document }: LinkedDocumentsProps) {
  const { t } = useTranslation();
  const { data: children } = useChildDocuments(document.id);

  const parentIds = document.parentDocIds?.length > 0
    ? document.parentDocIds
    : document.parentDocId
      ? [document.parentDocId]
      : [];

  const hasParents = parentIds.length > 0;
  const hasChildren = (children?.length ?? 0) > 0;

  if (!hasParents && !hasChildren) return null;

  return (
    <Card>
      <CardContent className="pt-4 pb-4 space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">{t('sales.linkedDocuments')}</h3>

      {/* Parent documents */}
      {hasParents && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{t('sales.parentDocuments')}</p>
          {parentIds.map((pid) => (
            <ParentDocLink key={pid} parentId={pid} />
          ))}
        </div>
      )}

      {/* Child documents */}
      {hasChildren && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{t('sales.childDocuments')}</p>
          {children!.map((child) => (
            <Link
              key={child.id}
              to={getDocPath(child)}
              className="block rounded-lg border px-3 py-2.5 hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ArrowDown size={14} className="text-muted-foreground shrink-0" />
                <Badge variant="outline" className="shrink-0">{t(DOC_TYPE_LABELS[child.docType])}</Badge>
                <span className="text-sm font-medium truncate">{child.number}</span>
                <span className="ms-auto text-sm font-medium whitespace-nowrap">{formatPrice(child.totalTtc)} DT</span>
              </div>
              <div className="mt-1 ps-[calc(14px+0.5rem)] text-xs text-muted-foreground">
                {formatDate(child.docDate)}
              </div>
            </Link>
          ))}
        </div>
      )}
      </CardContent>
    </Card>
  );
}
